// Phase 39 — MindStein Workspace Preview.
// Renders directly below the bot composer. Three groups:
//   1. Quick Reply       — two static Sample Outputs
//   2. Cross-Fusion      — one live example (existing frozen fusion)
//   3. Deep Synthesis    — the two existing example bot sessions
// Every card carries a "SAMPLE OUTPUT" label so nothing looks live.
import { Link } from "react-router-dom";
import { ArrowRight, Brain, GitMerge, Sparkles } from "lucide-react";
import QUICK_REPLY_SAMPLES from "../data/quickReplySamples";
import LIVE_FUSION_EXAMPLE from "../data/liveFusionExample";
import { EXAMPLE_SESSIONS } from "../data/exampleBotSessions";

function SampleLabel() {
  return (
    <span className="inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-[0.2em] border border-amber-400/40 bg-amber-400/10 text-amber-200 px-1.5 py-0.5">
      SAMPLE OUTPUT
    </span>
  );
}

function GroupHeader({ n, icon: Icon, title, blurb }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300 flex items-center gap-2">
        <Icon size={11} /> {n} · {title}
      </div>
      <p className="mt-1 text-[12px] text-zinc-400 leading-snug max-w-3xl">
        {blurb}
      </p>
    </div>
  );
}

function QuickReplyCard({ s }) {
  const a = s.answer;
  return (
    <div
      data-testid={`workspace-quick-reply-${s.id}`}
      className="border border-[#27272A] bg-[#0A0A0A] p-4"
    >
      <div className="flex items-center gap-2">
        <SampleLabel />
        <span className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">
          {s.kind.replace("_", " ")}
        </span>
      </div>
      <div className="mt-2 text-[11.5px] font-mono uppercase tracking-wider text-zinc-500">
        PROMPT
      </div>
      <p className="text-[13px] text-zinc-200 leading-snug">{s.prompt}</p>
      <div className="mt-3 text-[11.5px] font-mono uppercase tracking-wider text-emerald-300">
        QUICK_REPLY
      </div>
      <p className="text-[13.5px] text-white leading-snug">{a.headline}</p>
      {a.why_now && (
        <p className="mt-2 text-[12.5px] text-zinc-300 leading-relaxed">
          <span className="text-emerald-300 font-mono uppercase text-[9.5px] mr-2">
            WHY NOW
          </span>
          {a.why_now}
        </p>
      )}
      {a.state_of_the_field && (
        <p className="mt-2 text-[12.5px] text-zinc-300 leading-relaxed">
          <span className="text-emerald-300 font-mono uppercase text-[9.5px] mr-2">
            STATE OF THE FIELD
          </span>
          {a.state_of_the_field}
        </p>
      )}
      {a.shortest_path && (
        <>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
            SHORTEST_PATH
          </div>
          <ol className="mt-1 space-y-1 list-decimal list-inside text-[12.5px] text-zinc-300 leading-relaxed">
            {a.shortest_path.map((x, i) => <li key={i}>{x}</li>)}
          </ol>
        </>
      )}
      {a.three_load_bearing_facts && (
        <>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
            THREE LOAD-BEARING FACTS
          </div>
          <ul className="mt-1 space-y-1 list-disc list-inside text-[12.5px] text-zinc-300 leading-relaxed">
            {a.three_load_bearing_facts.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </>
      )}
      {a.what_a_serious_team_would_do_next && (
        <>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
            WHAT_A_SERIOUS_TEAM_WOULD_DO_NEXT
          </div>
          <ul className="mt-1 space-y-1 list-disc list-inside text-[12.5px] text-zinc-300 leading-relaxed">
            {a.what_a_serious_team_would_do_next.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </>
      )}
      {a.first_customers && (
        <p className="mt-3 text-[12.5px] text-zinc-300 leading-relaxed">
          <span className="text-emerald-300 font-mono uppercase text-[9.5px] mr-2">
            FIRST CUSTOMERS
          </span>
          {a.first_customers}
        </p>
      )}
      {a.what_to_test_first && (
        <p className="mt-2 text-[12.5px] text-zinc-300 leading-relaxed">
          <span className="text-emerald-300 font-mono uppercase text-[9.5px] mr-2">
            TEST FIRST
          </span>
          {a.what_to_test_first}
        </p>
      )}
      {a.research_workflow_note && (
        <p className="mt-3 border-l-2 border-amber-400/60 pl-2 text-[11.5px] text-zinc-400 leading-snug">
          {a.research_workflow_note}
        </p>
      )}
    </div>
  );
}

function CrossFusionCard() {
  const ex = LIVE_FUSION_EXAMPLE;
  const fc = ex.fused_concept;
  return (
    <div
      data-testid="workspace-cross-fusion"
      className="border border-[#27272A] bg-[#0A0A0A] p-4"
    >
      <div className="flex items-center gap-2">
        <SampleLabel />
        <span className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">
          CROSS-FUSION
        </span>
      </div>
      <div className="mt-2 text-[11.5px] font-mono uppercase tracking-wider text-zinc-500">
        INPUTS
      </div>
      <ul className="text-[12.5px] text-zinc-300 leading-snug list-disc list-inside">
        {ex.input_platforms.map((p, i) => <li key={i}>{p.title}</li>)}
      </ul>
      <div className="mt-3 text-[11.5px] font-mono uppercase tracking-wider text-emerald-300">
        FUSION OPPORTUNITY
      </div>
      <p className="text-[13.5px] text-white leading-snug">{fc.title}</p>
      <p className="mt-1 text-[13px] text-zinc-200 leading-relaxed">
        {fc.one_liner}
      </p>
      <p className="mt-2 text-[12.5px] text-zinc-300 leading-relaxed">
        {fc.combined_use_case}
      </p>
      <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
        DESIGN SUGGESTIONS
      </div>
      <ul className="mt-1 space-y-1 list-disc list-inside text-[12.5px] text-zinc-300 leading-relaxed">
        {fc.design_suggestions.slice(0, 4).map((x, i) => <li key={i}>{x}</li>)}
      </ul>
      <div className="mt-3 text-[11.5px] text-zinc-500">
        Two Emergent-ready build prompts and the full Fusion Opportunity view
        are available inside the Intelligence Layer.
      </div>
      <div className="mt-3">
        <Link
          to="/innovation"
          data-testid="workspace-cross-fusion-open"
          className="inline-flex items-center gap-1 h-8 px-3 border border-[#27272A] text-zinc-200 text-[10.5px] font-mono uppercase tracking-wider hover:bg-[#141414] hover:text-white"
        >
          Open in Intelligence Layer <ArrowRight size={11} />
        </Link>
      </div>
    </div>
  );
}

function DeepSynthCard({ s }) {
  const assistant = s.messages.find((m) => m.role === "assistant");
  const st = assistant?.structured || {};
  return (
    <div
      data-testid={`workspace-deep-synth-${s.id}`}
      className="border border-[#27272A] bg-[#0A0A0A] p-4"
    >
      <div className="flex items-center gap-2">
        <SampleLabel />
        <span className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">
          DEEP SYNTHESIS · {(st.mode || "").toUpperCase()}
        </span>
      </div>
      <div className="mt-2 text-[11.5px] font-mono uppercase tracking-wider text-zinc-500">
        QUESTION
      </div>
      <p className="text-[12.5px] text-zinc-300 leading-snug">
        {s.messages.find((m) => m.role === "user")?.text}
      </p>
      <div className="mt-3 text-[11.5px] font-mono uppercase tracking-wider text-emerald-300">
        SYNTHESIS
      </div>
      <p className="text-[13.5px] text-white leading-snug">{st.title || s.title}</p>
      <p className="mt-1 text-[13px] text-zinc-200 leading-relaxed">
        {st.summary}
      </p>
      <div className="mt-3 text-[11.5px] text-zinc-500">
        Full Math-Core ranking, cross-domain bridges, TRL assessment and
        executive summary PDF appear here when Deep Synthesis opens.
      </div>
    </div>
  );
}

export default function WorkspacePreview() {
  return (
    <section
      data-testid="workspace-preview"
      className="mt-6 border border-[#27272A] bg-[#0E0E0E] p-4 sm:p-6 space-y-8"
    >
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
          EXPLORER_EXAMPLE_LIBRARY
        </div>
        <h2 className="mt-1 text-xl sm:text-2xl font-mono tracking-tight text-white">
          What MindStein produces
        </h2>
        <div
          data-testid="workspace-private-preview-status"
          className="mt-2 border-l-2 border-emerald-400 pl-3 py-1 text-[12.5px] text-zinc-100 leading-relaxed max-w-3xl"
        >
          <span className="text-emerald-300 font-mono uppercase text-[10px] tracking-[0.25em] mr-2">
            PRIVATE PREVIEW ·
          </span>
          Completed workflow examples are available to explore now. Live
          analysis is opening through controlled early access.
        </div>
        <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed max-w-3xl">
          Two Quick Reply examples, one Cross-Fusion example, and the
          existing Deep Synthesis examples. Every card is labeled
          &quot;Sample Output&quot; and is static — nothing here runs live.
        </p>
      </div>

      {/* Group 1 — Quick Reply */}
      <div>
        <GroupHeader
          n="1"
          icon={Sparkles}
          title="QUICK_REPLY"
          blurb="A single question in, a structured briefing out. Two samples: a platform-idea question and a technical-research question."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {QUICK_REPLY_SAMPLES.map((s) => (
            <QuickReplyCard key={s.id} s={s} />
          ))}
        </div>
      </div>

      {/* Group 2 — Cross-Fusion */}
      <div>
        <GroupHeader
          n="2"
          icon={GitMerge}
          title="CROSS-FUSION"
          blurb="Two or three platform ideas fuse into one concept with common patterns, a fusion opportunity, and design suggestions."
        />
        <CrossFusionCard />
      </div>

      {/* Group 3 — Deep Synthesis */}
      <div>
        <GroupHeader
          n="3"
          icon={Brain}
          title="DEEP_SYNTHESIS"
          blurb="Multi-pass, DOI-verified synthesis with ranking, cross-domain bridges, TRL, and an executive summary PDF."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {EXAMPLE_SESSIONS.map((s) => (
            <DeepSynthCard key={s.id} s={s} />
          ))}
        </div>
      </div>

      <div className="border-t border-[#161616] pt-4 text-[11.5px] text-zinc-500 leading-snug">
        For health or medical topics: MindStein supports research workflow
        exploration only. It does not provide clinical guidance and does not
        replace expert review.
      </div>
    </section>
  );
}
